import { ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { useNarrow } from '../../lib/use-narrow';
import { Breadcrumb, Crumb } from './Breadcrumb';
import { IconButton, Typography } from './ui';
import styles from './PageHeader.module.css';

// The page header from the Symphony Figma: a breadcrumb trail, the page title
// (in the display face, with an optional inline edit affordance), and a
// description — plus an inline slot for right-aligned actions. The team-summary
// pill (AgentsIslandBadge) is separate top-right chrome owned by AppFrame, so it
// is intentionally not part of this component.
export function PageHeader({
  title,
  description,
  breadcrumb,
  avatar,
  onEdit,
  editLabel,
  actions,
  actionsAlign = 'start',
  flush,
}: {
  // A plain string renders in the display type style; a node renders as-is (the
  // node owns its own display styling, e.g. the inline-editable workspace name).
  title: string | ReactNode;
  description?: ReactNode;
  breadcrumb?: Crumb[];
  avatar?: ReactNode;
  onEdit?: () => void;
  editLabel?: string;
  actions?: ReactNode;
  // `start` pins the actions to the top of the title row; `center` centres them on it and lets them wrap
  // under the title when the row is too narrow to hold both — for actions wider than a single button.
  actionsAlign?: 'start' | 'center';
  // Drop the standard 48px bottom gap — for callers that append their own chrome
  // (e.g. the workspace hero's tab bar) and own the gap to content themselves.
  flush?: boolean;
}) {
  // Mobile drops the title to the 32/34 Display-03 scale (Figma 4176-6999 /
  // 4944-5613); desktop keeps the 40/44 Display.
  const narrow = useNarrow();
  return (
    <header className={`${styles.header}${flush ? ` ${styles.flush}` : ''}`}>
      {/* The back button lives here on desktop; on mobile the header back button covers it, so the
          slot (and its gap) is desktop-only. */}
      {!narrow && breadcrumb && breadcrumb.length > 0 && (
        <div className={styles.breadcrumb}>
          <Breadcrumb items={breadcrumb} />
        </div>
      )}

      <div className={`${styles.titleRow}${actionsAlign === 'center' ? ` ${styles.titleRowCentered}` : ''}`}>
        <div className={styles.titleGroup}>
          {avatar && <span className={styles.avatar}>{avatar}</span>}
          {typeof title === 'string' ? (
            <Typography variant={narrow ? 'h1' : 'display'} color="primary" noWrap={false}>
              {title}
            </Typography>
          ) : (
            title
          )}
          {onEdit && (
            <IconButton size="small" onClick={onEdit} aria-label={editLabel ?? ''} className={styles.edit}>
              <Pencil size={16} aria-hidden />
            </IconButton>
          )}
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>

      {description && (
        <Typography variant="p2" color="inherit" component="p" className={styles.description}>
          {description}
        </Typography>
      )}
    </header>
  );
}
